import { type FieldError } from 'react-hook-form';

import { type StructuredIssuePath } from '@/shared/components/structured/types/structured-issue.types';

import {
  fieldErrorMessage,
  type FieldErrorMessage,
} from '../field-error-message/field-error-message';

export interface StructuredFieldIssue {
  path: StructuredIssuePath;
  message: FieldErrorMessage;
}

/** What react-hook-form parks on the error object itself, not a member of the value. */
const BOOKKEEPING = new Set(['root', 'type', 'types', 'message', 'ref']);

function isLeaf(candidate: Record<string, unknown>): boolean {
  return typeof candidate['type'] === 'string' || typeof candidate['message'] === 'string';
}

/**
 * A nested react-hook-form error tree, flattened into one issue per leaf.
 *
 * These issues **already exist and were being thrown away**. `DynamicForm` runs
 * `zodResolver` over the whole field schema, so a bad URL inside `brand.links`
 * has produced `errors.value.website` since the settings form was built — and
 * `FieldRenderer` read only the top of that tree, found an object with no
 * `type`, and rendered one generic "Invalid value" under the entire editor. The
 * operator was told that *something* in twelve rows was wrong.
 *
 * It is a pure function, not a `validate` callback on the control, for the
 * reason that matters: the form already owns the verdict on whether a field is
 * valid, and a control that computed its own would be a second source of truth
 * for the same question — which is how a field ends up painted red while the
 * form believes it is clean.
 *
 * Each leaf keeps `fieldErrorMessage`'s discriminated result rather than a
 * string, so a Zod code stays a catalogue KEY (U8: Zod's own messages are
 * English constants) and a server message stays verbatim text.
 */
export function structuredIssuesOf(error: unknown): StructuredFieldIssue[] {
  const issues: StructuredFieldIssue[] = [];

  const walk = (node: unknown, path: StructuredIssuePath): void => {
    if (typeof node !== 'object' || node === null) return;

    if (Array.isArray(node)) {
      node.forEach((element, index) => {
        walk(element, [...path, index]);
      });
      return;
    }

    const record = node as Record<string, unknown>;

    if (isLeaf(record)) {
      issues.push({ path, message: fieldErrorMessage(record as FieldError, false) });
      return;
    }

    for (const [key, child] of Object.entries(record)) {
      if (BOOKKEEPING.has(key)) continue;
      walk(child, [...path, key]);
    }
  };

  walk(error, []);

  return issues;
}
